import { AxiosResponse } from "axios";

import loggingService from "@app/util/logging";

const PRINCIPAL_VERSION_HEADER = "x-mmc-principal-version";

// How long a rise can still be the user's own after their save answered. The first response to a
// request sent after the save carries the rise, so this only covers a save nothing follows soon.
const OWN_CHANGE_WINDOW_MS = 10_000;

export interface VersionRise {
  isOwnChange: boolean;
}

interface RequestOutcome {
  // missing when the request got no response, e.g. a network error or a cancel
  headers?: AxiosResponse["headers"];
  succeeded: boolean;
}

interface OwnChangeWindow {
  expiresAt: number;
  // the last request sent before the save answered: a response to a later one ends the window
  lastRequestIdBeforeAnswer: number;
  saveRequestId: number;
}

export class PrincipalVersionTracker {
  private firstRequestIdOfSession = 1;
  private highestVersion: number | undefined;
  private lastRequestId = 0;
  private ownChangeWindow: OwnChangeWindow | undefined;
  private raiseListener: ((rise: VersionRise) => void) | undefined;
  // saves in flight that can change the user's own access, e.g. an org rename
  private readonly saveRequestIds = new Set<number>();

  setRaiseListener = (listener: ((rise: VersionRise) => void) | undefined) => {
    this.raiseListener = listener;
  };

  // Returns the id endRequest takes
  startRequest = (changesAccess: boolean): number => {
    this.lastRequestId += 1;
    if (changesAccess) {
      this.saveRequestIds.add(this.lastRequestId);
    }
    return this.lastRequestId;
  };

  endRequest = (requestId: number, { headers, succeeded }: RequestOutcome) => {
    // a request sent before the last reset belongs to the session it ended
    if (requestId < this.firstRequestIdOfSession) {
      return;
    }
    this.observe(requestId, headers);

    const isSave = this.saveRequestIds.delete(requestId);
    if (
      headers &&
      this.ownChangeWindow &&
      requestId > this.ownChangeWindow.lastRequestIdBeforeAnswer
    ) {
      this.ownChangeWindow = undefined;
    }
    // The server stamps a response with the version its request started with, so a save comes
    // back with the old one and the version it raised arrives on a later response
    if (isSave && succeeded) {
      this.ownChangeWindow = {
        expiresAt: Date.now() + OWN_CHANGE_WINDOW_MS,
        lastRequestIdBeforeAnswer: this.lastRequestId,
        saveRequestId: requestId,
      };
    }
  };

  reset = () => {
    this.firstRequestIdOfSession = this.lastRequestId + 1;
    this.highestVersion = undefined;
    this.ownChangeWindow = undefined;
    this.saveRequestIds.clear();
  };

  // A request sent before the save went out started before the save changed anything
  private isOwnChange(requestId: number) {
    if ([...this.saveRequestIds].some((saveRequestId) => saveRequestId < requestId)) {
      return true;
    }
    const window = this.ownChangeWindow;
    return !!window && requestId > window.saveRequestId && Date.now() < window.expiresAt;
  }

  // A response sent before a change can arrive after it, so only a version above every one seen
  // so far is a change. The first version of a session is its starting point, not a change.
  private observe(requestId: number, headers: AxiosResponse["headers"] | undefined) {
    const header = headers?.[PRINCIPAL_VERSION_HEADER];
    // digits only, as mobile reads it: Number() alone would also take "1e3" or " 12 "
    const version = typeof header === "string" && /^\d+$/.test(header) ? Number(header) : NaN;
    if (!Number.isSafeInteger(version)) {
      return;
    }

    if (this.highestVersion === undefined) {
      this.highestVersion = version;
      return;
    }

    if (version > this.highestVersion) {
      this.highestVersion = version;
      const rise: VersionRise = { isOwnChange: this.isOwnChange(requestId) };
      // this runs inside every request's own result chain, so a failure here must not fail it
      try {
        this.raiseListener?.(rise);
      } catch (error) {
        loggingService.error("principal version listener failed", { error });
      }
    }
  }
}

const principalVersionTracker = new PrincipalVersionTracker();

export default principalVersionTracker;
